import type { Bout } from './bout';
import type { PunchStats } from './types';

/** How an opponent fights, from his card. */
export interface BrainSettings {
  readonly pauseMinSeconds: number;
  readonly pauseMaxSeconds: number;
  readonly blockChance: number;
  readonly reactionSeconds: number;
  /** How often he hits back as soon as his guard comes down after a punch he blocked. */
  readonly counterChance: number;
  /** Stamina he keeps back: he waits rather than punch himself empty. */
  readonly staminaReserve: number;
  /** How much more often he blocks for each punch the player threw in the last two seconds beyond two: he reads a flurry. */
  readonly blockPerFlurryPunch: number;
  /** How often an attack is a combo: the next punches follow as soon as he can throw them, not after his pause. */
  readonly comboChance: number;
  /** The most punches in one combo. */
  readonly comboMax: number;
  readonly favourites: readonly { readonly punch: PunchStats; readonly weight: number }[];
}

export function brainSettings(settings: BrainSettings): BrainSettings {
  if (settings.pauseMinSeconds < 0 || settings.pauseMaxSeconds < settings.pauseMinSeconds)
    throw new RangeError('The pause goes from a shorter to a longer time');
  for (const key of ['blockChance', 'counterChance', 'comboChance'] as const)
    if (settings[key] < 0 || settings[key] > 1) throw new RangeError(`${key} goes from 0 to 1`);
  if (settings.reactionSeconds < 0 || settings.staminaReserve < 0) throw new RangeError('Times and stamina cannot be negative');
  if (!Number.isInteger(settings.comboMax) || settings.comboMax < 1) throw new RangeError('A combo is at least one punch');
  if (settings.favourites.some((f) => f.weight < 0)) throw new RangeError("A punch's weight cannot be negative");
  return settings;
}

/** Numbers 0..1, from a seed, so a bout can be played again exactly. */
export type Random = () => number;

export function seededRandom(seed: number): Random {
  let state = seed >>> 0 || 1;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// How long he keeps his guard up once the punch he blocked has arrived.
const holdAfterSeconds = 0.2;
// How far back he remembers the player's punches when reading a flurry.
const flurrySeconds = 2;

/**
 * The opponent's decisions: his favourite punches after a pause, a block against the player's punches when he is
 * quick enough, a counter now and then after a block. He plays by the player's rules, through the player's commands.
 */
export class OpponentBrain {
  private untilAttack = 0;
  private reacting = false;
  private reactionLeft = 0;
  private holding = false;
  private lowering = false;
  private holdLeft = 0;
  private nextPunch: PunchStats | null = null;
  /** Punches still to come in the combo he is throwing. */
  private comboLeft = 0;
  private clock = 0;
  private readonly playerPunchTimes: number[] = [];

  constructor(private readonly bout: Bout, private readonly settings: BrainSettings, private readonly random: Random) {
    bout.punchStarted.add(({ side }) => this.onPunchStarted(side));
    bout.punchResolved.add((report) => {
      if (report.attacker !== 'player') return;
      // Caught clean in the middle of a combo: the rest of it is gone, and he starts his pause again.
      if (report.outcome === 'clean' && this.comboLeft > 0) {
        this.comboLeft = 0;
        this.untilAttack = this.pause();
      }
      // Too slow: the punch arrived before his guard came up, so he does not raise it after the fact.
      this.reacting = false;
      if (!this.holding) return;
      this.lowering = true;
      this.holdLeft = holdAfterSeconds;
      // The puncher is still recovering from the punch he blocked: now and then he hits back as soon as his guard is down.
      if (report.outcome === 'blocked' && this.random() < settings.counterChance) this.untilAttack = 0;
    });
    bout.roundStarted.add(() => this.startOver());
    this.startOver();
  }

  tick(seconds: number): void {
    this.clock += seconds;
    if (this.bout.phase !== 'round') return;
    // His next attack keeps coming while he blocks: otherwise every blocked punch would push it back.
    this.untilAttack -= seconds;
    if (this.reacting) {
      this.reactionLeft -= seconds;
      if (this.reactionLeft > 0) return;
      this.reacting = false;
      this.holding = this.bout.submit('opponent', { hand: 'left', action: 'blockStart' });
      return;
    }
    if (this.holding) {
      if (!this.lowering) return;
      this.holdLeft -= seconds;
      if (this.holdLeft > 0) return;
      this.bout.submit('opponent', { hand: 'left', action: 'blockEnd' });
      this.holding = this.lowering = false;
      return;
    }
    const me = this.bout.opponent;
    if (!me.canPunch || this.untilAttack > 0) return;
    this.nextPunch ??= this.pick();
    if (!this.nextPunch) return;
    // Winded: he waits for his breath rather than punch himself empty.
    if (me.stamina < this.nextPunch.staminaCost + this.settings.staminaReserve) return;
    if (this.bout.submit('opponent', { hand: this.nextPunch.hand, action: this.nextPunch.action })) {
      this.nextPunch = null;
      if (this.comboLeft > 0) this.comboLeft--;
      else if (this.settings.comboMax > 1 && this.random() < this.settings.comboChance) this.comboLeft = 1 + Math.floor(this.random() * (this.settings.comboMax - 1));
      this.untilAttack = this.comboLeft > 0 ? 0 : this.pause();
    }
  }

  private onPunchStarted(side: string): void {
    if (side !== 'player') return;
    this.playerPunchTimes.push(this.clock);
    while (this.playerPunchTimes[0] < this.clock - flurrySeconds) this.playerPunchTimes.shift();
    if (this.reacting || this.holding || this.bout.opponent.state !== 'ready') return;
    const flurry = Math.max(0, this.playerPunchTimes.length - 2);
    if (this.random() >= Math.min(0.95, this.settings.blockChance + flurry * this.settings.blockPerFlurryPunch)) return;
    this.reacting = true;
    this.reactionLeft = this.settings.reactionSeconds;
  }

  private startOver(): void {
    this.untilAttack = this.pause();
    this.reacting = this.holding = this.lowering = false;
    this.nextPunch = null;
    this.comboLeft = 0;
  }

  private pause(): number {
    return this.settings.pauseMinSeconds + this.random() * (this.settings.pauseMaxSeconds - this.settings.pauseMinSeconds);
  }

  private pick(): PunchStats | null {
    const total = this.settings.favourites.reduce((sum, f) => sum + f.weight, 0);
    if (total === 0) return null;
    let roll = this.random() * total;
    for (const favourite of this.settings.favourites) {
      roll -= favourite.weight;
      if (roll < 0) return favourite.punch;
    }
    return this.settings.favourites[this.settings.favourites.length - 1].punch;
  }
}
